import { DecimalPipe } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { CompaniesArchivedNoteDialogComponent } from 'src/app/companies/companies-archived-note-dialog/companies-archived-note-dialog.component';
import { CompaniesNotesDialogComponent } from 'src/app/companies/companies-notes-dialog/companies-notes-dialog.component';
import { CompaniesOriginDialogComponent } from 'src/app/companies/companies-origin-dialog/companies-origin-dialog.component';
import { CompaniesProductCategoryDialogComponent } from 'src/app/companies/companies-product-category-dialog/companies-product-category-dialog.component';
import { CompaniesSalesNetworkDialogComponent } from 'src/app/companies/companies-sales-network-dialog/companies-sales-network-dialog.component';
import { CompaniesSalesReferenceEmailDialogComponent } from 'src/app/companies/companies-sales-reference-email-dialog/companies-sales-reference-email-dialog.component';
import { CompaniesSubdomainDialogComponent } from 'src/app/companies/companies-subdomain-dialog/companies-subdomain-dialog.component';
import { Company } from 'src/app/core/companies.service';
import { DialogService } from 'src/app/core/dialog.service';
import { ErrorService, SplittypayError } from 'src/app/core/error.service';
import { UtilsService } from 'src/app/core/utils.service';

@Component({
  selector: 'app-company-details-general-info',
  templateUrl: './company-details-general-info.component.html',
  styleUrls: ['./company-details-general-info.component.scss'],
})
export class CompanyDetailsGeneralInfoComponent {
  @Input() company?: Company;
  @Output() refresh: EventEmitter<void> = new EventEmitter<void>();
  constructor(
    private utils_service: UtilsService,
    private dialog_service: DialogService,
    private error_service: ErrorService,
    private decimal_pipe: DecimalPipe,
    private translate: TranslateService,
  ) {}

  getCompanyStatusLabel(): string {
    if (!this.company?.status) return '';
    return this.utils_service.getCompanyStatusLabel(this.company.status, this.company?.is_partially_enabled);
  }

  getCompanyStatusBackgroundClass(): { [key: string]: boolean } {
    if (!this.company) return {};
    return this.utils_service.getCompanyStatusBackgroundClass(this.company);
  }

  getCompanyName(): string {
    if (!this.company?.business_name) {
      return '-';
    }

    if (this.company?.business_name.length > 30) {
      return this.company?.business_name.slice(0, 27) + '...';
    }

    return this.company?.business_name;
  }

  getCompanyProductCategoryLabel(): string {
    if (!this.company?.product_category) return '-';
    return this.utils_service.getCompanyProductCategoryLabel(this.company.product_category);
  }

  getCompanySalesChannelLabel(): string {
    if (!this.company?.sales_channel) return '-';
    return this.utils_service.getCompanySalesChannelLabel(this.company.sales_channel);
  }

  onSetProductCategoryClick(): void {
    this.dialog_service
      .openDialog(CompaniesProductCategoryDialogComponent, {
        company_id: this.company?.company_id,
        product_category: this.company?.product_category,
      })
      ?.afterClosed()
      .subscribe(dialog_result => {
        if (dialog_result && dialog_result.success) {
          this.refresh.emit();
        }

        if (dialog_result && dialog_result.error) {
          const processed_error: SplittypayError = dialog_result.error;
          this.error_service.processApiError(processed_error.code, processed_error.message);
        }
      });
  }

  isSalesNetworkIDEditable(): boolean {
    return !this.company?.is_sales_network_id_editing_disabled;
  }

  onSetSalesNetworkIdClick(): void {
    this.dialog_service
      .openDialog(CompaniesSalesNetworkDialogComponent, {
        company_id: this.company?.company_id,
        sales_network_id: this.company?.sales_network_id,
      })
      ?.afterClosed()
      .subscribe(dialog_result => {
        if (dialog_result && dialog_result.success) {
          this.refresh.emit();
        }

        if (dialog_result && dialog_result.error) {
          const processed_error: SplittypayError = dialog_result.error;
          this.error_service.processApiError(processed_error.code, processed_error.message);
        }
      });
  }

  onSetArchivedNoteClick(): void {
    this.dialog_service
      .openDialog(CompaniesArchivedNoteDialogComponent, {
        company_id: this.company?.company_id,
        note: this.company?.archived_note,
      })
      ?.afterClosed()
      .subscribe(dialog_result => {
        if (dialog_result && dialog_result.success) {
          this.refresh.emit();
        }

        if (dialog_result && dialog_result.error) {
          const processed_error: SplittypayError = dialog_result.error;
          this.error_service.processApiError(processed_error.code, processed_error.message);
        }
      });
  }

  getOwnerFullName(): string {
    if (this.company) {
      const full_name = `${this.company.owner_firstname} ${this.company.owner_lastname}`;
      if (full_name.length > 30) {
        return full_name.slice(0, 27) + '...';
      } else {
        return full_name;
      }
    } else {
      return '-';
    }
  }

  getRevenuesRange(): string {
    if (this.company) {
      if (this.company.min_revenue != 999999999) {
        const min_revenue: string | null = this.formatNumber(this.company.min_revenue);
        const max_revenue: string | null = this.formatNumber(this.company.max_revenue);
        return this.company ? `${min_revenue}€ - ${max_revenue}€` : '-';
      } else {
        return this.translate.instant('STORES.MORE_REVENUE');
      }
    }
    return '-';
  }

  formatNumber(digits: number): string | null {
    return this.decimal_pipe.transform(digits, '0.2-2', 'it-IT');
  }

  getAllowedDomain(): string {
    if (this.company) {
      const domains_string = this.company.allowed_domains ? this.company.allowed_domains.join(', ') : '-';
      if (domains_string.length > 30) {
        return domains_string.slice(0, 27) + '...';
      } else {
        return domains_string;
      }
    } else {
      return '-';
    }
  }

  onSetOriginClick(): void {
    this.dialog_service
      .openDialog(CompaniesOriginDialogComponent, {
        company_id: this.company?.company_id,
        company_origin: this.company?.origin,
      })
      ?.afterClosed()
      .subscribe(dialog_result => {
        if (dialog_result && dialog_result.success) {
          this.refresh.emit();
        }

        if (dialog_result && dialog_result.error) {
          const processed_error: SplittypayError = dialog_result.error;
          this.error_service.processApiError(processed_error.code, processed_error.message);
        }
      });
  }

  getCompanyOriginLabel(): string {
    if (!this.company?.origin) return '-';
    return this.utils_service.getCompanyOriginLabel(this.company.origin);
  }

  getArchivedNote(): string {
    if (this.company) {
      const archived_note = this.company.archived_note ? this.company.archived_note : '-';
      if (archived_note.length > 30) {
        return archived_note.slice(0, 27) + '...';
      } else {
        return archived_note;
      }
    } else {
      return '-';
    }
  }

  getNotes(): string {
    if (this.company) {
      const stand_by_notes = this.company.stand_by_note ? this.company.stand_by_note : '-';
      if (stand_by_notes.length > 30) {
        return stand_by_notes.slice(0, 27) + '...';
      } else {
        return stand_by_notes;
      }
    } else {
      return '-';
    }
  }

  onSetCustomDomainClick(): void {
    this.dialog_service
      .openDialog(CompaniesSubdomainDialogComponent, { company_id: this.company?.company_id })
      ?.afterClosed()
      .subscribe(dialog_result => {
        if (dialog_result && dialog_result.success) {
          this.refresh.emit();
        }

        if (dialog_result && dialog_result.error) {
          const processed_error: SplittypayError = dialog_result.error;
          this.error_service.processApiError(processed_error.code, processed_error.message);
        }
      });
  }

  onSetNotesClick(): void {
    this.dialog_service
      .openDialog(CompaniesNotesDialogComponent, {
        company_id: this.company?.company_id,
        stand_by_notes: this.company?.stand_by_note,
      })
      ?.afterClosed()
      .subscribe(dialog_result => {
        if (dialog_result && dialog_result.success) {
          this.refresh.emit();
        }

        if (dialog_result && dialog_result.error) {
          const processed_error: SplittypayError = dialog_result.error;
          this.error_service.processApiError(processed_error.code, processed_error.message);
        }
      });
  }

  onSetSalesReferenceEmailClick(): void {
    this.dialog_service
      .openDialog(CompaniesSalesReferenceEmailDialogComponent, {
        company_id: this.company?.company_id,
        sales_reference_email: this.company?.sales_reference_email,
      })
      ?.afterClosed()
      .subscribe(dialog_result => {
        if (dialog_result && dialog_result.success) {
          this.refresh.emit();
        }

        if (dialog_result && dialog_result.error) {
          const processed_error: SplittypayError = dialog_result.error;
          this.error_service.processApiError(processed_error.code, processed_error.message);
        }
      });
  }
}
